import { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, Sparkles, User, Bot } from "lucide-react";
import api from "@/lib/api";

// Robustnost: historie chatu může přijít jako ne-pole nebo s null-položkami.
const asArr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === "object") : []);

/**
 * ClientDiscussionTab — v3.8 Elite Light redesign.
 * Per-client AI chat with full context awareness (smlouvy, rodina, hlasovky).
 * Cream-paper messages on warm cream background. Gold (#D4AF37) reserved for
 * AI brand cue only; user bubble uses cognac. NO dark navy.
 */
export default function ClientDiscussionTab({ contactId, contactName }) {
  // sessionId musí patřit AKTUÁLNÍMU klientovi — jinak by se po přepnutí klienta
  // posílaly zprávy do session předchozího klienta (bleed kontextu na backendu).
  // Stabilní per klient — s Date.now() se historie vlákna NIKDY nenačetla zpět.
  const sessionId = useMemo(() => `s-${contactId}`, [contactId]);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Přepnutí klienta → vyčisti staré zprávy (žádná stale konverzace jiného klienta).
    setMessages([]);
    (async () => {
      try {
        const { data } = await api.get(`/api/client-discussion/${contactId}`, {
          params: { session_id: sessionId },
        });
        if (!cancelled) setMessages(asArr(data));
      } catch { /* fresh session */ }
    })();
    return () => { cancelled = true; };
  }, [contactId, sessionId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    const userTurn = { role: "user", content: msg, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userTurn]);
    setSending(true);
    try {
      const { data } = await api.post(`/api/client-discussion/${contactId}`, {
        message: msg,
        session_id: sessionId,
      });
      setMessages((m) => [...m, { role: "assistant", content: data?.reply || "(prázdná odpověď)", created_at: new Date().toISOString() }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Promiň, AI právě nedostupná. Zkus to znovu.", error: true, created_at: new Date().toISOString() }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const suggestions = [
    "Co jsme naposledy řešili?",
    `Navrhni argumenty pro ${contactName?.split(" ")[0] || "klienta"} na příští schůzku.`,
    "Které produkty mu chybí?",
    "Připrav follow-up zprávu po dnešní schůzce.",
  ];

  const TEXT_BASE = "var(--ink)"; /* tokeny motivu (CRM-09) — dřív krémový ostrov i v Midnight */
  const TEXT_MUTED = "var(--muted)";
  const COGNAC = "var(--accent)";
  const GOLD = "var(--warn-text)";
  const CREAM = "var(--surface-2)";
  const PAPER = "var(--surface)";

  return (
    <div className="flex flex-col h-[calc(100vh-260px)] min-h-[420px]" data-testid="discussion-tab">
      {/* Header */}
      <div
        className="rounded-t-2xl px-5 py-3 relative overflow-hidden"
        style={{
          background: PAPER,
          border: "1px solid color-mix(in srgb, var(--warn) 30%, transparent)",
          borderBottom: "none",
        }}
      >
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: "linear-gradient(90deg, transparent, var(--warn) 50%, transparent)" }} />
        <div className="flex items-center gap-2 text-popisek uppercase tracking-[0.22em] font-semibold" style={{ color: GOLD }}>
          <Sparkles size={12} /> AI Asistent · zná tohoto klienta
        </div>
      </div>

      {/* Messages */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto px-4 py-4 space-y-3"
        style={{
          background: CREAM,
          borderLeft: "1px solid var(--line)",
          borderRight: "1px solid var(--line)",
        }}
      >
        {messages.length === 0 && !sending && (
          <div className="py-8 text-center">
            <Sparkles size={28} className="mx-auto mb-3" style={{ color: GOLD }} />
            <div className="text-sm mb-2 font-semibold" style={{ color: TEXT_BASE }}>Zeptej se na cokoliv o klientovi</div>
            <div className="text-drobny mb-4" style={{ color: TEXT_MUTED }}>AI má kompletní kontext (smlouvy, rodina, hlasovky)</div>
            <div className="flex flex-wrap gap-2 justify-center max-w-md mx-auto">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  onClick={() => send(s)}
                  data-testid={`discussion-suggestion-${i}`}
                  className="px-3 py-1.5 rounded-full text-drobny font-medium transition-colors"
                  style={{
                    background: "color-mix(in srgb, var(--warn) 8%, var(--surface))",
                    border: "1px solid color-mix(in srgb, var(--warn) 30%, transparent)",
                    color: "var(--warn-text)",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = "color-mix(in srgb, var(--warn) 16%, var(--surface))"}
                  onMouseLeave={(e) => e.currentTarget.style.background = "color-mix(in srgb, var(--warn) 8%, var(--surface))"}
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        )}

        <AnimatePresence initial={false}>
          {messages.map((m, i) => {
            const isUser = m.role === "user";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : ""}`}
                data-testid={`discussion-msg-${m.role}-${i}`}
              >
                <div
                  className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{
                    background: isUser ? "var(--accent-soft)" : "color-mix(in srgb, var(--warn) 15%, var(--surface))",
                    border: `1px solid ${isUser ? "color-mix(in srgb, var(--accent) 40%, transparent)" : "color-mix(in srgb, var(--warn) 40%, transparent)"}`,
                  }}
                >
                  {isUser
                    ? <User size={12} style={{ color: COGNAC }} />
                    : <Bot size={12} style={{ color: GOLD }} />}
                </div>
                <div
                  className="max-w-[78%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap"
                  style={
                    isUser
                      ? {
                          background: "var(--accent)",
                          color: "var(--on-accent)",
                          borderTopRightRadius: 4,
                          boxShadow: "var(--shadow-card)",
                        }
                      : m.error
                        ? {
                            background: "color-mix(in srgb, var(--negative) 6%, var(--surface))",
                            border: "1px solid color-mix(in srgb, var(--negative) 30%, transparent)",
                            color: "var(--negative-text)",
                            borderTopLeftRadius: 4,
                          }
                        : {
                            background: PAPER,
                            border: "1px solid var(--line)",
                            color: TEXT_BASE,
                            borderTopLeftRadius: 4,
                            boxShadow: "none",
                          }
                  }
                >
                  {m.content}
                </div>
              </motion.div>
            );
          })}
        </AnimatePresence>

        {sending && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex gap-2.5 items-center">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: "color-mix(in srgb, var(--warn) 15%, var(--surface))", border: "1px solid color-mix(in srgb, var(--warn) 40%, transparent)" }}
            >
              <Bot size={12} style={{ color: GOLD }} />
            </div>
            <div
              className="rounded-2xl px-3.5 py-2.5"
              style={{
                background: PAPER,
                border: "1px solid var(--line)",
                borderTopLeftRadius: 4,
              }}
            >
              <span className="inline-flex gap-1">
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: GOLD, animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: GOLD, animationDelay: "150ms" }} />
                <span className="w-1.5 h-1.5 rounded-full animate-bounce" style={{ background: GOLD, animationDelay: "300ms" }} />
              </span>
            </div>
          </motion.div>
        )}
      </div>

      {/* Input */}
      <div
        className="rounded-b-2xl px-3 py-3 flex items-center gap-2"
        style={{
          background: "var(--surface-2)",
          border: "1px solid var(--line)",
          borderTop: "1px solid var(--line)",
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKey}
          placeholder={`Zeptej se na ${contactName?.split(" ")[0] || "klienta"}…`}
          data-testid="discussion-input"
          disabled={sending}
          className="flex-1 rounded-full px-4 py-2 text-sm outline-none transition-colors disabled:opacity-50"
          style={{
            background: PAPER,
            border: "1px solid var(--line)",
            color: TEXT_BASE,
          }}
        />
        <button
          onClick={() => send()}
          disabled={!input.trim() || sending}
          data-testid="discussion-send"
          className="w-10 h-10 rounded-full flex items-center justify-center disabled:opacity-40 transition-transform hover:scale-105"
          style={{
            background: "var(--warn-text)",
            color: "var(--surface)",
            boxShadow: "var(--shadow-card)",
          }}
          aria-label="Odeslat"
        >
          <Send size={14} />
        </button>
      </div>
    </div>
  );
}
